// Two-column shell with grouped sidebar, sticky topbar, and a mobile drawer
// (Radix Sheet) holding the same nav. Brand mark + theme toggle + Clerk
// UserButton + inline collapse toggle anchor the sidebar's identity rows.
//
// Sidebar geometry is mathematical, not eyeballed:
//
//   The desktop sidebar is `--sidebar-rail-w` (64px) when collapsed and
//   `--sidebar-expanded-w` (256px) when expanded. Every row inside —
//   brand, nav links, theme toggle, collapse toggle, user avatar — wraps
//   its leading icon-bearing element in <RailSlot> from sidebar-nav.tsx.
//   The slot is a fixed 48px wide and centers its child, so the icon's
//   X-position is invariant: 32px from the rail's left edge in both states.
//   The result: toggling animates the rail width AROUND the icon column,
//   not past it. Any drift between rows = a row that isn't using <RailSlot>.
//
// Tooltip timing is sidebar-scoped:
//
//   The global TooltipProvider in lib/providers.tsx uses delayDuration=200
//   for incidental tooltips. The sidebar wraps its content in a *nested*
//   provider with delayDuration=120 + skipDelayDuration=0 — so when the
//   user is in collapsed mode and tooltips are the ONLY label affordance,
//   the first hover is snappier and consecutive hovers feel instant.
//
// Keyboard:
//   • ⌘B / Ctrl+B (primary) and [ (legacy) toggle collapse — see
//     lib/sidebar-state.tsx. The sidebar's own collapse toggle in the
//     footer mirrors this.

import { useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { UserButton, useUser } from '@clerk/clerk-react';
import { Menu, PanelLeftClose, PanelLeftOpen, Sparkles } from 'lucide-react';
import { Button } from '../ui/button.js';
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from '../ui/sheet.js';
import { Separator } from '../ui/separator.js';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip.js';
import { ErrorBoundary } from '../error-boundary.js';
import { SidebarNav, RailSlot } from './sidebar-nav.js';
import { ThemeToggle } from './theme-toggle.js';
import { SubscriptionStatusBanner } from '../billing/subscription-status-banner.js';
import { cn } from '../../lib/utils.js';
import { useSidebarState } from '../../lib/sidebar-state.js';

// Brand mark uses a dedicated full-rail-width slot (`w-[var(--sidebar-rail-w)]`
// = 64px) since the brand row spans edge-to-edge with no `mx-2`. The violet
// square inside is centered, which lands its center at 32px from the rail
// edge — same column as every nav-row icon below. The wordmark goes sr-only
// when collapsed; the square stays.
function BrandMark({ collapsed = false }: { collapsed?: boolean }) {
  return (
    <div className="flex items-center">
      <span className="flex h-14 w-[var(--sidebar-rail-w)] shrink-0 items-center justify-center">
        <span
          aria-hidden
          className="flex h-7 w-7 items-center justify-center rounded-md bg-primary text-primary-foreground shadow-sm"
        >
          <Sparkles className="h-[var(--sidebar-icon-size)] w-[var(--sidebar-icon-size)]" />
        </span>
      </span>
      <span
        className={cn(
          'pr-4 text-sm font-semibold tracking-tight',
          collapsed ? 'sr-only' : 'whitespace-nowrap',
        )}
      >
        Cortex
      </span>
    </div>
  );
}

// Footer rows use the same shape as <NavRow> in sidebar-nav.tsx — `mx-2`
// outer + <RailSlot> at the start + a label that becomes sr-only when
// collapsed. Reusing the geometry is what keeps the icon column invariant
// from the brand mark down to the user avatar.
function SidebarFooter({ collapsed = false, onToggle }: { collapsed?: boolean; onToggle?: () => void }) {
  const { user } = useUser();
  const displayName = user?.fullName ?? user?.primaryEmailAddress?.emailAddress ?? 'Account';
  const subline = user?.primaryEmailAddress?.emailAddress;

  return (
    <div className="mt-auto border-t py-2">
      {/* Inline collapse toggle (desktop only — mobile uses the Sheet
          trigger in the topbar). Lives in the sidebar so a collapsed user
          can expand from the rail itself, instead of mousing all the way
          to the topbar. The topbar duplicate stays for now (kept for users
          already accustomed to that location). */}
      {onToggle && (
        <FooterRow>
          <RailSlot>
            <CollapseToggleButton collapsed={collapsed} onToggle={onToggle} />
          </RailSlot>
          <span
            className={cn(
              'pr-3 text-sm text-muted-foreground',
              collapsed ? 'sr-only' : 'whitespace-nowrap',
            )}
          >
            {collapsed ? 'Expand' : 'Collapse'}
          </span>
        </FooterRow>
      )}
      <FooterRow>
        <RailSlot>
          <ThemeToggle />
        </RailSlot>
        <span
          className={cn(
            'pr-3 text-sm text-muted-foreground',
            collapsed ? 'sr-only' : '',
          )}
        >
          Theme
        </span>
      </FooterRow>
      <Separator className="mx-2 my-2" />
      <FooterRow>
        <RailSlot>
          {/* Wrap UserButton in a Tooltip in collapsed mode so hovering the
              avatar surfaces the user's identity (mirroring nav rows). In
              expanded mode the name+email are visible next to the avatar
              already, so no tooltip there. */}
          {collapsed ? (
            <Tooltip>
              <TooltipTrigger asChild>
                {/* The UserButton is a button itself; wrapping it in a
                    span lets Radix attach its trigger props without
                    nested-button HTML. */}
                <span className="inline-flex">
                  <UserButton
                    appearance={{
                      elements: { rootBox: 'h-8 w-8', userButtonAvatarBox: 'h-8 w-8' },
                    }}
                  />
                </span>
              </TooltipTrigger>
              <TooltipContent side="right">
                <p className="font-medium">{displayName}</p>
                {subline && subline !== displayName && (
                  <p className="text-muted-foreground">{subline}</p>
                )}
              </TooltipContent>
            </Tooltip>
          ) : (
            <UserButton
              appearance={{
                elements: { rootBox: 'h-8 w-8', userButtonAvatarBox: 'h-8 w-8' },
              }}
            />
          )}
        </RailSlot>
        <div
          className={cn(
            'min-w-0 flex-1 pr-3',
            collapsed ? 'sr-only' : '',
          )}
        >
          <p className="truncate text-sm font-medium">{displayName}</p>
          {subline && subline !== displayName && (
            <p className="truncate text-xs text-muted-foreground">{subline}</p>
          )}
        </div>
      </FooterRow>
    </div>
  );
}

// Tiny row primitive shared by every footer entry. Same shape as <NavRow>
// in sidebar-nav.tsx (mx-2 + h-[var(--sidebar-row-h)] + flex items-center)
// so the icon column stays aligned with the nav above. The row itself owns
// no interactive state — children render their own buttons/triggers inside
// the leading <RailSlot>.
function FooterRow({ children }: { children: React.ReactNode }) {
  return (
    <div className="mx-2 flex h-[var(--sidebar-row-h)] items-center">
      {children}
    </div>
  );
}

// Collapse-toggle as an icon-sm Button. Sized 32×32 (h-8 w-8) by default
// from the `icon-sm` size variant — matches the slot's vertical centering.
// Tooltip surfaces both the action and the keyboard shortcut.
function CollapseToggleButton({
  collapsed,
  onToggle,
}: {
  collapsed: boolean;
  onToggle: () => void;
}) {
  const Icon = collapsed ? PanelLeftOpen : PanelLeftClose;
  const label = collapsed ? 'Expand sidebar' : 'Collapse sidebar';
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={label}
          aria-pressed={collapsed}
          onClick={onToggle}
          className="ring-offset-card"
        >
          <Icon
            aria-hidden
            className="h-[var(--sidebar-icon-size)] w-[var(--sidebar-icon-size)]"
          />
        </Button>
      </TooltipTrigger>
      <TooltipContent side="right">
        {label}{' '}
        <span className="ml-1 text-muted-foreground">
          (<kbd>⌘</kbd>/<kbd>Ctrl</kbd>+<kbd>B</kbd>)
        </span>
      </TooltipContent>
    </Tooltip>
  );
}

export function AppShell() {
  const { pathname } = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { isCollapsed, toggle } = useSidebarState();

  return (
    <div className="flex h-full">
      {/* Desktop sidebar — width animates between rail and expanded widths.
          The icon column inside stays at 32px from the rail edge throughout
          (the <RailSlot> primitive guarantees this; see sidebar-nav.tsx).
          overflow-hidden clips off-screen labels during the transition;
          md:flex hides on mobile (Sheet drawer is the mobile equivalent). */}
      <TooltipProvider delayDuration={120} skipDelayDuration={0}>
        <aside
          data-collapsed={isCollapsed}
          className={cn(
            'hidden shrink-0 flex-col overflow-hidden border-r bg-card transition-[width] duration-200 ease-out md:flex',
          )}
          style={{
            width: isCollapsed ? 'var(--sidebar-rail-w)' : 'var(--sidebar-expanded-w)',
          }}
          aria-label="Primary navigation"
        >
          <div className="flex h-14 items-center border-b">
            <BrandMark collapsed={isCollapsed} />
          </div>
          <div className="flex flex-1 flex-col overflow-y-auto overflow-x-hidden">
            <SidebarNav collapsed={isCollapsed} />
          </div>
          <SidebarFooter collapsed={isCollapsed} onToggle={toggle} />
        </aside>
      </TooltipProvider>

      <div className="flex min-w-0 flex-1 flex-col">
        {/* Topbar reads as L1 chrome (same surface as sidebar + cards). The
            `bg-card/85` keeps a frosted-glass effect when content scrolls
            under the sticky bar. */}
        <header className="sticky top-0 z-30 flex h-14 items-center justify-between gap-3 border-b bg-card/85 px-4 backdrop-blur-sm md:px-6">
          <div className="flex items-center gap-3 md:hidden">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon-sm" aria-label="Open menu">
                  <Menu aria-hidden />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="flex w-72 flex-col p-0">
                <SheetTitle className="sr-only">Navigation</SheetTitle>
                <TooltipProvider delayDuration={120} skipDelayDuration={0}>
                  <div className="flex h-14 items-center border-b">
                    <BrandMark />
                  </div>
                  <div className="flex-1 overflow-y-auto">
                    <SidebarNav
                      onNavigate={() => {
                        setMobileOpen(false);
                      }}
                    />
                  </div>
                  <SidebarFooter />
                </TooltipProvider>
              </SheetContent>
            </Sheet>
            <BrandMark />
          </div>
          {/* Desktop topbar: empty left side. The collapse toggle now lives
              in the sidebar footer (reachable from the rail itself), so the
              topbar no longer carries it. */}
          <div className="hidden flex-1 md:block" />
          <div className="flex items-center gap-2 md:hidden">
            <UserButton
              appearance={{ elements: { rootBox: 'h-8 w-8', userButtonAvatarBox: 'h-8 w-8' } }}
            />
          </div>
        </header>

        <SubscriptionStatusBanner />

        <main className="flex-1 overflow-y-auto overflow-x-hidden">
          <div className="mx-auto w-full min-w-0 max-w-screen-2xl p-4 sm:p-6 lg:p-8">
            <ErrorBoundary key={pathname}>
              <Outlet />
            </ErrorBoundary>
          </div>
        </main>
      </div>
    </div>
  );
}
